import {
  SHORTCUTS_FOLDER_NAME,
  VIEWS_DIR,
  type ShortcutManifestEntry,
} from './build-shortcut-manifest';
import {isMaticShortcutFile} from './shortcut-file-formats';

/**
 * The File System Access layer — picking a folder, holding write permission,
 * and writing a manifest into it.
 *
 * Everything failure-shaped is returned as a typed outcome rather than thrown,
 * because most of these "failures" are ordinary user choices (cancelling the
 * picker) that must not surface as errors.
 */

declare global {
  // Not in TypeScript's DOM lib as of 5.9 — Chrome/Edge only.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    showDirectoryPicker?: (options?: {
      id?: string;
      mode?: 'read' | 'readwrite';
      startIn?: string;
    }) => Promise<FileSystemDirectoryHandle>;
  }

  // The permission methods are a non-standard extension, also untyped.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface FileSystemHandle {
    queryPermission?: (descriptor?: {
      mode?: 'read' | 'readwrite';
    }) => Promise<PermissionState>;
    requestPermission?: (descriptor?: {
      mode?: 'read' | 'readwrite';
    }) => Promise<PermissionState>;
  }

  // The async-iterator half of the directory handle is implemented in
  // Chrome/Edge but absent from the DOM lib.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface FileSystemDirectoryHandle {
    entries: () => AsyncIterableIterator<[string, FileSystemHandle]>;
  }
}

export type PickFolderOutcome =
  | {status: 'picked'; handle: FileSystemDirectoryHandle}
  | {status: 'cancelled'}
  | {status: 'blocked'}
  | {status: 'failed'; message: string};

export type WriteOutcome =
  | {status: 'written'; fileCount: number}
  | {status: 'denied'}
  | {status: 'folder-missing'}
  | {status: 'failed'; message: string};

export function isFileSystemAccessSupported(): boolean {
  return typeof globalThis.window?.showDirectoryPicker === 'function';
}

function errorName(error: unknown): string {
  return error instanceof Error ? error.name : '';
}

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message.length > 0
    ? error.message
    : 'Something went wrong writing the shortcuts.';
}

/**
 * Prompt for a parent folder, then create (or reopen) our own directory inside
 * it. Must be called from a user gesture.
 */
export async function pickShortcutsDirectory(): Promise<PickFolderOutcome> {
  if (!isFileSystemAccessSupported()) return {status: 'blocked'};

  // Support was already confirmed above — re-read here only so TypeScript can
  // narrow this to a callable, not to re-check support.
  const picker = globalThis.window?.showDirectoryPicker;
  if (!picker) return {status: 'blocked'};

  try {
    const parent = await picker({
      id: 'matic-shortcuts',
      mode: 'readwrite',
      startIn: 'desktop',
    });
    // Ours by construction — see SHORTCUTS_FOLDER_NAME. Pruning below is only
    // safe because we created this directory rather than the user picking it.
    const handle = await parent.getDirectoryHandle(SHORTCUTS_FOLDER_NAME, {
      create: true,
    });
    return {status: 'picked', handle};
  } catch (error) {
    // Cancelling the picker is a choice, not a failure.
    if (errorName(error) === 'AbortError') return {status: 'cancelled'};
    // Enterprise policy can disable the API, and some directories are refused
    // outright (system paths, the browser's own profile directory).
    if (
      errorName(error) === 'SecurityError' ||
      errorName(error) === 'NotAllowedError' ||
      errorName(error) === 'TypeError'
    ) {
      return {status: 'blocked'};
    }

    return {status: 'failed', message: errorMessage(error)};
  }
}

/**
 * Confirm we may still write to a stored folder.
 *
 * **Call this first, synchronously from the click.** The handle survives in
 * IndexedDB across reloads but the permission grant does not — on a fresh page
 * load `queryPermission` returns `'prompt'`, and `requestPermission` only works
 * inside a transient user activation. Awaiting a query or building the manifest
 * before calling it spends the activation and the prompt silently fails.
 */
export async function ensureWritePermission(
  handle: FileSystemDirectoryHandle
): Promise<'granted' | 'denied'> {
  const descriptor = {mode: 'readwrite'} as const;

  try {
    const current = await handle.queryPermission?.(descriptor);
    if (current === 'granted') return 'granted';

    const requested = await handle.requestPermission?.(descriptor);
    // Undefined means the browser doesn't implement the extension at all, in
    // which case the handle either works or throws on write — assume granted
    // and let the write report the truth.
    return requested === 'denied' ? 'denied' : 'granted';
  } catch {
    return 'denied';
  }
}

async function directoryFor(
  root: FileSystemDirectoryHandle,
  segments: string[]
): Promise<FileSystemDirectoryHandle> {
  let current = root;
  for (const segment of segments) {
    // Sequential by necessity — each level is the parent of the next.
    // eslint-disable-next-line no-await-in-loop
    current = await current.getDirectoryHandle(segment, {create: true});
  }

  return current;
}

async function writeFile(
  directory: FileSystemDirectoryHandle,
  filename: string,
  contents: string
): Promise<void> {
  const fileHandle = await directory.getFileHandle(filename, {create: true});
  const writable = await fileHandle.createWritable();
  await writable.write(contents);
  await writable.close();
}

async function directoryEntries(
  directory: FileSystemDirectoryHandle
): Promise<Array<[string, FileSystemHandle]>> {
  const result: Array<[string, FileSystemHandle]> = [];
  for await (const entry of directory.entries()) result.push(entry);
  return result;
}

function fileEntries(
  entries: Array<[string, FileSystemHandle]>
): Array<[string, FileSystemFileHandle]> {
  return entries.filter(
    (entry): entry is [string, FileSystemFileHandle] => entry[1].kind === 'file'
  );
}

/**
 * Whether a file handle points at something this feature wrote, checked by
 * reading the file back rather than trusting the filename. Unreadable files
 * are treated as foreign — the safe failure mode is leaving a file alone, not
 * deleting one we couldn't actually verify.
 */
async function isOwnedShortcutFile(
  handle: FileSystemFileHandle
): Promise<boolean> {
  try {
    const file = await handle.getFile();
    return isMaticShortcutFile(await file.text());
  } catch {
    return false;
  }
}

/**
 * Remove stale files from one directory, without ever touching a file this
 * feature didn't create.
 *
 * The directory is only "ours by construction" at the moment
 * `pickShortcutsDirectory` creates it — after that it's an ordinary folder on
 * the user's real disk, and `removeEntry` does not go through the OS trash.
 * So a name being absent from `keepFilenames` is necessary but not
 * sufficient: a file only gets deleted once its *content* also carries the
 * `GeneratedBy` marker every shortcut is stamped with. A file the user (or a
 * sync client) dropped in here survives an Update untouched.
 *
 * The ownership reads are independent of each other and safe to run
 * concurrently — reads can't corrupt anything — so they're batched with
 * `Promise.all`. The qualifying removals stay sequential, matching the same
 * conservative one-at-a-time spirit as `writeManifest`'s write loop below.
 */
async function pruneStaleFiles(
  directory: FileSystemDirectoryHandle,
  files: Array<[string, FileSystemFileHandle]>,
  keepFilenames: Set<string>
): Promise<void> {
  const candidates = files.filter(([name]) => !keepFilenames.has(name));
  const ownership = await Promise.all(
    candidates.map(
      async ([name, handle]) =>
        [name, await isOwnedShortcutFile(handle)] as const
    )
  );

  for (const [name, isOwned] of ownership) {
    if (!isOwned) continue;
    // eslint-disable-next-line no-await-in-loop
    await directory.removeEntry(name);
  }
}

/**
 * Remove anything in our directory that the manifest no longer describes, so
 * a removed Page's shortcut disappears rather than lingering as a stale link
 * — subject to the ownership check in `pruneStaleFiles`.
 *
 * Two flat passes — root, then the one subdirectory this feature ever creates
 * (`Views/`) — rather than a generic arbitrary-depth recursion: there is
 * exactly one level of nesting today, and no second owned subdirectory type
 * planned. Anything at root that isn't `Views/` is left completely alone —
 * not opened, not recursed into, not deleted. Toggling "Include Views" off
 * still cleans out an existing `Views/`, since `keepInViews` is simply empty
 * in that case.
 */
async function pruneStaleEntries(
  root: FileSystemDirectoryHandle,
  entries: ShortcutManifestEntry[]
): Promise<void> {
  const keepAtRoot = new Set<string>();
  const keepInViews = new Set<string>();

  for (const entry of entries) {
    if (entry.dir.length === 0) {
      keepAtRoot.add(entry.filename);
    } else {
      keepInViews.add(entry.filename);
    }
  }

  const rootEntries = await directoryEntries(root);
  await pruneStaleFiles(root, fileEntries(rootEntries), keepAtRoot);

  const viewsEntry = rootEntries.find(
    ([name, handle]) => name === VIEWS_DIR && handle.kind === 'directory'
  );
  if (!viewsEntry) return;

  const viewsDirectory = viewsEntry[1] as FileSystemDirectoryHandle;
  const viewsEntries = await directoryEntries(viewsDirectory);
  await pruneStaleFiles(viewsDirectory, fileEntries(viewsEntries), keepInViews);

  const remaining = await directoryEntries(viewsDirectory);
  if (remaining.length === 0) await root.removeEntry(VIEWS_DIR);
}

/**
 * Write every manifest entry into the owned directory, pruning whatever the
 * manifest no longer covers.
 *
 * Writes are **sequential on purpose**. `createWritable` stages through a temp
 * swap file committed on `close()`, and firing thirty of those at once inside a
 * OneDrive- or Dropbox-backed folder gives the sync client a pile of temps to
 * chew on. Files are tiny, so the serial cost is irrelevant.
 */
export async function writeManifest(
  root: FileSystemDirectoryHandle,
  entries: ShortcutManifestEntry[],
  onProgress?: (written: number, total: number) => void
): Promise<WriteOutcome> {
  try {
    await pruneStaleEntries(root, entries);

    let written = 0;
    for (const entry of entries) {
      // eslint-disable-next-line no-await-in-loop
      const directory = await directoryFor(root, entry.dir);
      // eslint-disable-next-line no-await-in-loop
      await writeFile(directory, entry.filename, entry.contents);
      written += 1;
      onProgress?.(written, entries.length);
    }

    return {status: 'written', fileCount: written};
  } catch (error) {
    // The folder was deleted since we stored the handle. (A *renamed* or
    // *moved* folder keeps working — Chrome tracks it by file id.)
    if (errorName(error) === 'NotFoundError') return {status: 'folder-missing'};
    if (errorName(error) === 'NotAllowedError') return {status: 'denied'};
    return {status: 'failed', message: errorMessage(error)};
  }
}
